"use client";

import Image from "next/image";
import { memo, useCallback, useState } from "react";
import { t } from "@/utils";
import resetIcon from "/public/assets/icons/reset-s--color-primary.svg";
import type { TFilterCategory } from "@/components";

type FilterCategoryProps = {
	readonly hasReset: boolean; // the category selection can be cleared
	readonly categories: TFilterCategory[];
	readonly onChange: (category: TFilterCategory | undefined) => void;
};

/**
 * Displays a list of provided categories to use for different filters
 * @constructor
 */
function FilterCategory(props: FilterCategoryProps) {
	const [selectedCategory, setSelectedCategory] = useState<
		TFilterCategory | undefined
	>();
	const onCategorySelect = useCallback(
		(category: TFilterCategory | undefined) => {
			if (typeof props.onChange === "function") {
				props.onChange(category);
			}
		},
		[props]
	);

	return (
		<>
			<ul
				className={`list-none ${(props.hasReset && selectedCategory && "mb-3 border-b pb-5") ?? ""}`}
				data-testid="filter-category-list"
			>
				{props.categories.map((category) => {
					return (
						<li
							key={category.id}
							className={`
							flex items-center gap-2 border-b-2 py-2 text-[14px] font-semibold  text-systemInfo
							hover:cursor-pointer hover:border-b-primaryHover hover:text-primaryHover 
							${category.id === selectedCategory?.id ? " border-b-systemPrimary text-systemPrimary" : "border-b-transparent"}
						`}
							data-testid={`filter-category-${category.label}`}
							onClick={() => {
								setSelectedCategory(category);
								onCategorySelect(category);
							}}
						>
							<category.icon />
							{`${category.label} (${category.nrResults ?? 0})`}
						</li>
					);
				})}
			</ul>

			{props.hasReset && selectedCategory && (
				<span
					className="hmc-button-s ml-1 font-semibold text-systemPrimary hover:cursor-pointer hover:underline"
					data-testid="filter-category-reset"
					onClick={() => {
						setSelectedCategory(undefined);
						onCategorySelect(undefined);
					}}
				>
					<Image
						src={resetIcon}
						alt={t("filters.reset-category")}
						width={14}
						height={14}
						className="-mt-[2px] mr-1 inline-block fill-systemPrimary"
						data-testid="filter-category-reset-icon"
					/>
					{t("filters.reset-category")}
				</span>
			)}
		</>
	);
}

export default memo(FilterCategory);
